import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../App';
import { useAuth } from '../AuthContext';
import { t } from '../i18n';
import LanguageToggle from '../components/game/LanguageToggle';
import Sidebar from '../components/game/Sidebar';
import { motion } from 'framer-motion';
import { UserPlus, Trash2, Mail, Users, Shield } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function SchoolTeachersPage() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { user, token } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [teachers, setTeachers] = useState({ teachers: [], count: 0, max: 10 });
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviting, setInviting] = useState(false);
  const [toast, setToast] = useState('');
  const [loading, setLoading] = useState(true);
  const isEN = language === 'en';
  const headers = { Authorization: `Bearer ${token}` };
  const headersJson = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => {
    if (!token) { navigate('/login'); return; }
    fetchTeachers();
  }, [token]);

  const fetchTeachers = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/school/teachers`, { headers });
      if (res.ok) setTeachers(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const inviteTeacher = async () => {
    if (!inviteEmail.trim()) return;
    setInviting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/school/invite`, {
        method: 'POST', headers: headersJson,
        body: JSON.stringify({ email: inviteEmail.trim(), name: inviteName.trim() })
      });
      if (res.ok) {
        setToast(isEN ? 'Teacher invited!' : 'Guru dijemput!');
        setInviteEmail(''); setInviteName('');
        fetchTeachers();
      } else {
        const e = await res.json();
        setToast(e.detail || 'Error');
      }
    } catch { setToast('Error'); }
    setInviting(false);
    setTimeout(() => setToast(''), 3000);
  };

  const removeTeacher = async (email) => {
    if (!window.confirm(isEN ? `Remove ${email}?` : `Buang ${email}?`)) return;
    await fetch(`${BACKEND_URL}/api/school/teachers/${email}`, { method: 'DELETE', headers });
    fetchTeachers();
  };

  if (!user || user.plan !== 'school') {
    return (
      <div className="dashboard-page">
        <div className="dashboard-content" style={{ textAlign: 'center', paddingTop: '60px' }}>
          <Shield size={48} color="#9ca3af" />
          <h2 style={{ marginTop: 16 }}>{isEN ? 'School Plan Required' : 'Pelan Sekolah Diperlukan'}</h2>
          <p style={{ color: '#64748b', marginTop: 8 }}>{isEN ? 'Upgrade to School plan to manage teacher accounts.' : 'Naik taraf ke pelan Sekolah untuk urus akaun guru.'}</p>
          <button className="setup-back-btn" onClick={() => navigate('/pricing')} style={{ marginTop: 16 }}>{isEN ? 'View Plans' : 'Lihat Pelan'}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <header className="dashboard-header">
        <button className="sidebar-toggle" onClick={() => setSidebarOpen(true)} aria-label="Menu">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" /></svg>
        </button>
        <h1 className="dashboard-header-title">{isEN ? 'Teacher Accounts' : 'Akaun Guru'}</h1>
        <LanguageToggle className="dashboard-lang-toggle" />
      </header>

      <div className="dashboard-content">
        {loading ? <div className="lb-loading"><div className="lb-spinner" /></div> : (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div className="admin-teachers-header">
              <h3>{isEN ? 'Manage Teacher Accounts' : 'Urus Akaun Guru'}</h3>
              <p>{isEN ? 'Invite up to 10 teachers to use PANTASS with your School plan.' : 'Jemput sehingga 10 guru untuk guna PANTASS dengan pelan Sekolah anda.'}</p>
              <div className="admin-teachers-counter">
                <span>{teachers.count}/{teachers.max}</span>
                <span>{isEN ? 'teachers' : 'guru'}</span>
              </div>
            </div>

            {toast && (
              <div style={{ background: '#dcfce7', color: '#166534', padding: '10px 16px', borderRadius: 12, fontWeight: 700, marginBottom: 12, textAlign: 'center' }}>
                {toast}
              </div>
            )}

            {/* Invite Form */}
            <div className="admin-invite-form" data-testid="invite-teacher-form">
              <h4><UserPlus size={16} /> {isEN ? 'Invite Teacher' : 'Jemput Guru'}</h4>
              <div className="admin-invite-row">
                <input className="admin-invite-input" type="email" value={inviteEmail}
                  onChange={e => setInviteEmail(e.target.value)} placeholder={isEN ? 'Teacher email' : 'E-mel guru'}
                  data-testid="invite-email" />
                <input className="admin-invite-input" value={inviteName}
                  onChange={e => setInviteName(e.target.value)} placeholder={isEN ? 'Name (optional)' : 'Nama (pilihan)'}
                  data-testid="invite-name" />
                <button className="admin-invite-btn" onClick={inviteTeacher}
                  disabled={inviting || !inviteEmail.trim() || teachers.count >= teachers.max}
                  data-testid="invite-btn">
                  {inviting ? '...' : <><Mail size={16} /> {isEN ? 'Invite' : 'Jemput'}</>}
                </button>
              </div>
              {teachers.count >= teachers.max && (
                <p className="admin-invite-limit">{isEN ? 'Maximum 10 teachers reached.' : 'Had maksimum 10 guru dicapai.'}</p>
              )}
            </div>

            {/* Teachers List */}
            {teachers.teachers.length === 0 ? (
              <div className="lb-empty">{isEN ? 'No teachers invited yet. Invite your first teacher!' : 'Tiada guru dijemput lagi. Jemput guru pertama anda!'}</div>
            ) : (
              <div className="admin-teachers-list">
                {teachers.teachers.map((t, i) => (
                  <div key={i} className="admin-teacher-card" data-testid={`teacher-${i}`}>
                    <div className="admin-teacher-avatar" style={{ background: '#009688' }}>
                      {(t.teacher_name || '?')[0].toUpperCase()}
                    </div>
                    <div className="admin-teacher-info">
                      <div className="admin-teacher-name">{t.teacher_name || t.teacher_email}</div>
                      <div className="admin-teacher-email">{t.teacher_email}</div>
                      <div className="admin-teacher-meta">
                        <span className="admin-teacher-school">{t.school_name}</span>
                        <span className="admin-teacher-status">{t.status === 'active' ? (isEN ? 'Active' : 'Aktif') : t.status}</span>
                      </div>
                    </div>
                    <button className="admin-teacher-remove" onClick={() => removeTeacher(t.teacher_email)}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* How it works */}
            <div className="admin-teachers-info">
              <h4>{isEN ? 'How it works' : 'Cara berfungsi'}</h4>
              <ul>
                <li>{isEN ? 'Enter teacher\'s email and click Invite' : 'Masukkan e-mel guru dan klik Jemput'}</li>
                <li>{isEN ? 'If they have a PANTASS account, their plan auto-upgrades to School' : 'Jika mereka ada akaun PANTASS, pelan mereka auto naik taraf ke School'}</li>
                <li>{isEN ? 'If not, they\'ll get School access when they register with that email' : 'Jika tidak, mereka akan dapat akses School bila daftar dengan e-mel tersebut'}</li>
                <li>{isEN ? 'Removing a teacher downgrades them back to Free plan' : 'Membuang guru akan turunkan mereka ke pelan Percuma'}</li>
              </ul>
            </div>

            <button className="setup-back-btn" onClick={() => navigate('/')} style={{ marginTop: 16 }}>&larr; {t('backToHome', language)}</button>
          </motion.div>
        )}
      </div>
    </div>
  );
}
